import { create } from 'zustand';
import { MainProduct, VariantProduct, S3UploadedProduct, VariantAngle, VariantDef, UpscalerItem } from '../types/studio';
import { GARMENT_PROMPTS } from '../constants/garmentPrompts';

interface StudioState {
  // API Keys
  openaiKey: string;
  replicateKey: string;
  setOpenaiKey: (key: string) => void;
  setReplicateKey: (key: string) => void;

  // Main Generator State
  mainQueue: MainProduct[];
  setMainQueue: (queue: MainProduct[] | ((prev: MainProduct[]) => MainProduct[])) => void;
  updateMainProduct: (id: string, updates: Partial<MainProduct>) => void;
  addMainProduct: (product: MainProduct) => void;
  removeMainProduct: (id: string) => void;
  clearMainQueue: () => void;

  // Variant Generator State
  variantAngles: VariantAngle[];
  setVariantAngles: (angles: VariantAngle[] | ((prev: VariantAngle[]) => VariantAngle[])) => void;
  variantDefs: VariantDef[];
  setVariantDefs: (defs: VariantDef[] | ((prev: VariantDef[]) => VariantDef[])) => void;
  variantQueue: VariantProduct[];
  setVariantQueue: (queue: VariantProduct[] | ((prev: VariantProduct[]) => VariantProduct[])) => void;
  updateVariantProduct: (id: string, updates: Partial<VariantProduct>) => void;
  clearVariantQueue: () => void;

  // Generation Config State
  prompt: string;
  setPrompt: (p: string) => void;
  garmentType: string;
  setGarmentType: (g: string) => void;
  quality: string;
  setQuality: (q: string) => void;
  size: string;
  setSize: (s: string) => void;

  // DB Upload Phase State
  dbUploadQueue: S3UploadedProduct[];
  addDbUpload: (product: S3UploadedProduct) => void;
  updateDbUploadStatus: (rowId: string, status: 'pending' | 'uploaded') => void;

  // Upscaler State
  upscalerQueue: UpscalerItem[];
  addUpscalerItem: (item: UpscalerItem) => void;
  toggleUpscalerSelect: (id: string) => void;
  selectAllUpscaler: (select: boolean) => void;
  updateUpscalerItem: (id: string, updates: Partial<UpscalerItem>) => void;
  clearUpscalerQueue: () => void;
}

export const useStudioStore = create<StudioState>((set) => ({
  openaiKey: '',
  replicateKey: '',
  setOpenaiKey: (key) => set({ openaiKey: key }),
  setReplicateKey: (key) => set({ replicateKey: key }),

  mainQueue: [
    { id: crypto.randomUUID(), images: [], modelImage: undefined, productId: '', status: 'pending', reviewState: 'pending' },
    { id: crypto.randomUUID(), images: [], modelImage: undefined, productId: '', status: 'pending', reviewState: 'pending' }
  ],
  setMainQueue: (queue) => set((state) => ({ 
    mainQueue: typeof queue === 'function' ? queue(state.mainQueue) : queue 
  })),
  updateMainProduct: (id, updates) => set((state) => ({
    mainQueue: state.mainQueue.map(p => p.id === id ? { ...p, ...updates } : p)
  })),
  addMainProduct: (product) => set((state) => ({
    mainQueue: [...state.mainQueue, product]
  })),
  removeMainProduct: (id) => set((state) => ({
    mainQueue: state.mainQueue.filter(p => p.id !== id)
  })),
  clearMainQueue: () => set({ mainQueue: [] }),

  variantAngles: [
    { id: crypto.randomUUID(), label: 'Angle 1' }
  ],
  setVariantAngles: (angles) => set((state) => ({
    variantAngles: typeof angles === 'function' ? angles(state.variantAngles) : angles
  })),
  variantDefs: [
    { id: crypto.randomUUID(), label: 'Variant 1', productId: '', notes: '' }
  ],
  setVariantDefs: (defs) => set((state) => ({
    variantDefs: typeof defs === 'function' ? defs(state.variantDefs) : defs
  })),
  variantQueue: [],
  setVariantQueue: (queue) => set((state) => ({
    variantQueue: typeof queue === 'function' ? queue(state.variantQueue) : queue
  })),
  updateVariantProduct: (id, updates) => set((state) => ({
    variantQueue: state.variantQueue.map(p => p.id === id ? { ...p, ...updates } : p)
  })),
  clearVariantQueue: () => set({ variantQueue: [] }),

  prompt: GARMENT_PROMPTS.top,
  setPrompt: (p) => set({ prompt: p }),
  garmentType: 'top',
  setGarmentType: (g) => set({ garmentType: g }),
  quality: 'low',
  setQuality: (q) => set({ quality: q }),
  size: '1024x1024',
  setSize: (s) => set({ size: s }),

  dbUploadQueue: [],
  addDbUpload: (product) => set((state) => ({ dbUploadQueue: [...state.dbUploadQueue, product] })),
  updateDbUploadStatus: (rowId, status) => set((state) => ({
    dbUploadQueue: state.dbUploadQueue.map(p => p.rowId === rowId ? { ...p, status } : p)
  })),

  upscalerQueue: [],
  addUpscalerItem: (item) => set((state) => ({ upscalerQueue: [...state.upscalerQueue, item] })),
  toggleUpscalerSelect: (id) => set((state) => ({
    upscalerQueue: state.upscalerQueue.map(i => i.id === id ? { ...i, selected: !i.selected } : i)
  })),
  selectAllUpscaler: (select) => set((state) => ({
    upscalerQueue: state.upscalerQueue.map(i => ({ ...i, selected: select }))
  })),
  updateUpscalerItem: (id, updates) => set((state) => ({
    upscalerQueue: state.upscalerQueue.map(i => i.id === id ? { ...i, ...updates } : i)
  })),
  clearUpscalerQueue: () => set({ upscalerQueue: [] })
}));
